import { useState, type FormEvent } from 'react';
import { api, isCodeSent, type CodeSent, type Me } from '../api.ts';

/**
 * The door: signing in, signing up as a pupil, and the code that finishes either.
 *
 * Three screens in one component because they are one conversation. Splitting them across routes
 * would mean carrying the address in the URL, where it would sit in history and in every shared
 * link — and losing it on a refresh, stranding somebody who has the code but no way to use it.
 *
 * There is no "create a school" here: this install belongs to one. Signing up makes a pupil
 * account, and that account sees no course until a teacher enrols it.
 *
 * The sign-in failure message is the same for a wrong password and an unknown address, because
 * the server answers identically too.
 */

type Mode = 'login' | 'signup' | 'code';

export function LoginPage({ onSignedIn }: { onSignedIn: (me: Me) => void }): React.JSX.Element {
  const [mode, setMode] = useState<Mode>('login');
  /** Which conversation the code belongs to — the two verify endpoints are different. */
  const [pending, setPending] = useState<{ purpose: 'signup' | 'login'; sent: CodeSent } | null>(
    null,
  );

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(false);

  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  function askForCode(purpose: 'signup' | 'login', sent: CodeSent): void {
    setPending({ purpose, sent });
    setMode('code');
    setCode('');
    setError('');
    setNote(
      sent.email_delivered
        ? `Мы отправили код на ${sent.email}. Он действует ${sent.expires_in_minutes} минут.`
        : 'Письмо отправить не удалось. Обратитесь к преподавателю.',
    );
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError('');

    try {
      if (mode === 'signup') {
        askForCode('signup', await api.signUp({ email, password, full_name: fullName.trim() }));
        return;
      }

      if (mode === 'code') {
        const purpose = pending?.purpose ?? 'login';
        onSignedIn(
          purpose === 'signup'
            ? await api.verifySignup(email, code.trim())
            : await api.verifyLogin(email, code.trim(), remember),
        );
        return;
      }

      const result = await api.login(email, password);
      if (isCodeSent(result)) {
        askForCode('login', result);
        return;
      }

      onSignedIn(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  function switchTo(next: Mode): void {
    setMode(next);
    setError('');
    setNote('');
    setCode('');
  }

  const title =
    mode === 'signup'
      ? 'Регистрация ученика'
      : mode === 'code'
        ? 'Код из письма'
        : 'Вход в личный кабинет';

  return (
    <div className="center">
      <form className="auth stack" onSubmit={(e) => void submit(e)}>
        <div>
          <div className="brand" style={{ fontSize: '1.4rem', marginBottom: 6 }}>
            Junior<span>Prog</span>
          </div>
          <p className="muted">{title}</p>
        </div>

        <div className="card">
          {error ? <p className="error">{error}</p> : null}
          {note ? <p className="muted">{note}</p> : null}

          {mode === 'code' ? (
            <>
              <div className="field">
                <label htmlFor="code">Код</label>
                <input
                  id="code"
                  className="input code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  required
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/gu, ''))}
                  placeholder="000000"
                />
              </div>

              {pending?.purpose === 'login' ? (
                <label className="checkline">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  <span>Не спрашивать код на этом устройстве месяц</span>
                </label>
              ) : null}
            </>
          ) : (
            <>
              {mode === 'signup' ? (
                <div className="field">
                  <label htmlFor="full-name">Имя и фамилия</label>
                  <input
                    id="full-name"
                    className="input"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Пётр Иванов"
                  />
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="email">Почта</label>
                <input
                  id="email"
                  className="input"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="password">Пароль</label>
                <input
                  id="password"
                  className="input"
                  type="password"
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  required
                  minLength={mode === 'signup' ? 10 : undefined}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                {mode === 'signup' ? (
                  <span className="muted" style={{ fontSize: '0.8rem' }}>
                    Не короче 10 символов.
                  </span>
                ) : null}
              </div>
            </>
          )}

          <button className="btn" type="submit" disabled={busy} style={{ width: '100%' }}>
            {busy
              ? '…'
              : mode === 'signup'
                ? 'Зарегистрироваться'
                : mode === 'code'
                  ? 'Подтвердить'
                  : 'Войти'}
          </button>
        </div>

        {mode === 'code' ? (
          <p className="muted" style={{ fontSize: '0.84rem' }}>
            Код придёт с адреса info@juniorprog.kz. Проверьте папку «Спам».
            <br />
            <button type="button" className="linklike" onClick={() => switchTo('login')}>
              ← Начать заново
            </button>
          </p>
        ) : mode === 'signup' ? (
          <p className="muted" style={{ fontSize: '0.84rem' }}>
            После регистрации курсы появятся не сразу — их выдаёт преподаватель.
            <br />
            <button type="button" className="linklike" onClick={() => switchTo('login')}>
              ← У меня уже есть доступ
            </button>
          </p>
        ) : (
          <p className="muted" style={{ fontSize: '0.84rem' }}>
            <a className="linklike" href="/reset-password">
              Забыли пароль?
            </a>
            <br />
            <button type="button" className="linklike" onClick={() => switchTo('signup')}>
              Я ученик, зарегистрироваться
            </button>
          </p>
        )}
      </form>
    </div>
  );
}
